// Observation — what matters is what's WRONG: off-target and unmeasured KPIs
// lead. Two colour states only: in target = neutral ink, off = warn/err.
import { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { daysAgo } from '../utils/format';

type Tab = 'process' | 'kpi' | 'off';

/** Parse "> 0", "< 5", "95", "95 %" targets; null when unparsable → never flag. */
function offTarget(k: any): boolean | null {
  const v = k.latest?.value != null ? Number(k.latest.value) : null;
  if (v == null || !k.target) return null;
  const m = String(k.target).match(/([<>]=?)?\s*(-?\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  const bound = Number(m[2].replace(',', '.'));
  const op = m[1] ?? '>=';
  if (op === '>') return !(v > bound);
  if (op === '>=') return !(v >= bound);
  if (op === '<') return !(v < bound);
  if (op === '<=') return !(v <= bound);
  return null;
}

const stale = (k: any) => k.latest?.measured_at ? daysAgo(k.latest.measured_at) > 30 : true;

export function ObservationPage() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>('process');
  const [processes, setProcesses] = useState<any[] | null>(null);
  const [kpisByProcess, setKpisByProcess] = useState<Map<number, any[]>>(new Map());

  useEffect(() => {
    api.getProcesses().then(all => {
      const live = all.filter((p: any) => p.state === 'live' || p.in_inventory);
      setProcesses(live);
      for (const p of live) {
        api.getKpis({ process_id: String(p.id) })
          .then(ks => setKpisByProcess(prev => new Map(prev).set(p.id, ks)))
          .catch(() => {});
      }
    }).catch(() => setProcesses([]));
  }, []);

  const allKpis = useMemo(() => {
    const seen = new Map<number, { kpi: any; consumers: string[] }>();
    for (const [pid, ks] of kpisByProcess) {
      const proc = processes?.find(p => p.id === pid);
      for (const k of ks) {
        if (!seen.has(k.id)) seen.set(k.id, { kpi: k, consumers: [] });
        seen.get(k.id)!.consumers.push(proc?.process_code ?? String(pid));
      }
    }
    return [...seen.values()];
  }, [kpisByProcess, processes]);

  if (!processes) return <div className="page-loading"><div className="spinner" /></div>;
  if (processes.length === 0) return <div className="empty-state"><div className="empty-state-desc">{t('obs.empty')}</div></div>;

  const offCount = allKpis.filter(x => offTarget(x.kpi) === true).length;
  const staleCount = allKpis.filter(x => stale(x.kpi)).length;

  const tile = (k: any) => {
    const off = offTarget(k);
    const isStale = stale(k);
    return (
      <div key={k.id} className={`kpi-tile${off === true ? ' kpi-tile--off' : ''}`}>
        <div className="kpi-name">{k.name}{k.is_shared && <span className="badge badge-plain" style={{ marginLeft: 4 }}>{t('fiche.p4.shared')}</span>}</div>
        <div className="kpi-value">
          {k.latest?.value ?? k.latest?.value_text ?? '—'}{k.unit && k.latest ? ` ${k.unit}` : ''}
        </div>
        <div className="kpi-meta">
          {k.target ? t('obs.target', { t: k.target }) : ''}
          {k.latest?.measured_at
            ? isStale && <span className="text-warn"> · <Clock size={10} className="icon-inline" /> {t('obs.lastMeasure', { n: daysAgo(k.latest.measured_at) })}</span>
            : <span> · {t('obs.noMeasure')}</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="page-header">
        <div><h1 className="page-title">{t('shell.title.observe')}</h1></div>
      </div>

      <div className="stats-grid mb-24">
        <div className="stat-card"><div className="stat-value">{processes.length}</div><div className="stat-label">{t('obs.stat.live')}</div></div>
        <div className="stat-card"><div className="stat-value">{allKpis.length}</div><div className="stat-label">{t('obs.stat.kpis')}</div></div>
        <div className="stat-card"><div className="stat-value" style={offCount > 0 ? { color: 'var(--warn)' } : undefined}>{offCount}</div><div className="stat-label">{t('obs.stat.offTarget')}</div></div>
        <div className="stat-card"><div className="stat-value" style={staleCount > 0 ? { color: 'var(--warn)' } : undefined}>{staleCount}</div><div className="stat-label">{t('obs.stat.stale')}</div></div>
      </div>

      <div className="tabs mb-16">
        {([['process', t('obs.tab.byProcess')], ['kpi', t('obs.tab.byKpi')], ['off', t('obs.tab.offTarget')]] as [Tab, string][]).map(([k, label]) => (
          <button key={k} className={`tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>

      {tab === 'process' && processes.map(p => (
        <section key={p.id} className="mb-24">
          <h3 className="section-label mb-8">
            <a className="text-ink-1" href={`#/process/${p.id}`}>
              <span className="mono">{p.process_code}</span> {p.name}
            </a>
            {p.process_owner_name && <span className="text-ink-3 font-normal"> · {p.process_owner_name}</span>}
          </h3>
          {(kpisByProcess.get(p.id) ?? []).length === 0
            ? <p className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('obs.noKpis')}</p>
            : <div className="flex gap-12" style={{ flexWrap: 'wrap' }}>{(kpisByProcess.get(p.id) ?? []).map(tile)}</div>}
        </section>
      ))}

      {tab === 'kpi' && (
        <div className="flex gap-12" style={{ flexWrap: 'wrap' }}>
          {allKpis.map(({ kpi, consumers }) => (
            <div key={kpi.id} style={{ position: 'relative' }}>
              {tile(kpi)}
              <div className="text-xs text-ink-3" style={{ marginTop: 2 }}>{t('obs.usedByN', { n: consumers.length })} · {consumers.join(', ')}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'off' && (
        offCount === 0
          ? <div className="empty-state"><div className="empty-state-desc">{t('obs.allOnTarget')}</div></div>
          : <div className="flex gap-12" style={{ flexWrap: 'wrap' }}>
              {allKpis.filter(x => offTarget(x.kpi) === true).map(({ kpi }) => tile(kpi))}
            </div>
      )}
    </>
  );
}
